import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, ChevronDown, Building2, Plus, Menu, LogOut } from 'lucide-react';
import DashboardMobileDrawer from '../dashboard/DashboardMobileDrawer';
import DashboardMobileNav from '../dashboard/DashboardMobileNav';
import { useAuth } from '../../context/AuthContext';
import { flag } from '../../config';

/**
 * V5 Business Dashboard Header
 * Navy sticky header with logo, BUSINESS badge, centered navigation, and business switcher on right
 * Max width: 1280px
 */
const BusinessHeader = ({ user, userName, currentBusiness, businesses, onSwitchBusiness, activeDealsCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const switcherRef = useRef(null);
  
  const navLinks = [
    { path: '/business/dashboard', label: 'Dashboard' },
    { path: '/business/settings', label: 'Business Details' },
    { path: '/business/deals', label: 'Deals' },
    { path: '/business/subscription-manage', label: 'Subscription' }
  ];
  
  const isActive = (path) => {
    if (path === '/business/dashboard') {
      return location.pathname === '/business/dashboard' || location.pathname === '/business';
    }
    if (path === '/business/deals') {
      return location.pathname.includes('/business/deals');
    }
    return location.pathname === path;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (switcherRef.current && !switcherRef.current.contains(event.target)) {
        setShowSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Signing out was reachable only from the mobile drawer before (audit row 74).
  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const handleBusinessSelect = (businessId) => {
    if (onSwitchBusiness) {
      onSwitchBusiness(businessId);
    }
    setShowSwitcher(false);
  };

  // Purple color scheme for business dashboard
  const activeStyle = {
    backgroundColor: '#8b5cf6',
    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.5), 0 0 30px rgba(139, 92, 246, 0.3)'
  };

  // Prepare badges for mobile nav
  const badges = {
    activeDeals: activeDealsCount || null,
  };

  return (
    <>
      <header 
        className="bg-[#1e1b4b] sticky top-0 z-50"
        style={{ boxShadow: '0 4px 20px rgba(30, 27, 75, 0.3)' }}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            
            {/* Left: Logo + Badge */}
            <div className="flex items-center gap-3 md:gap-4">
              <Link to="/" className="flex items-center">
                <img 
                  src="/tappd-logo-white.png" 
                  alt="Tappd" 
                  className="h-7 md:h-8 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 32"><text x="0" y="24" fill="white" font-family="serif" font-size="24" font-weight="bold">tappd</text></svg>';
                  }}
                />
              </Link>
              <span 
                className="px-2.5 md:px-3 py-1 bg-[#8b5cf6] text-white text-[10px] md:text-xs font-bold rounded-md"
                style={{ boxShadow: '0 2px 10px rgba(139, 92, 246, 0.5), 0 0 20px rgba(139, 92, 246, 0.3)' }}
              >
                BUSINESS
              </span>
            </div>

            {/* Center: Navigation (Desktop only) */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    isActive(link.path) 
                      ? 'text-white font-semibold' 
                      : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                  }`}
                  style={isActive(link.path) ? activeStyle : {}}
                >
                  {link.label}
                </button>
              ))}
            </nav>
            
            {/* Right: Business Switcher + Homepage (Desktop) + Hamburger (Mobile) */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Business Switcher Dropdown - Hidden on mobile */}
              {/* Phase 1 (PD-25): the switcher only appears when the flag is on and there is something to switch to */}
              {flag('MULTI_BUSINESS') && businesses && businesses.length > 1 && (
                <div className="relative hidden md:block" ref={switcherRef}>
                  <button
                    onClick={() => setShowSwitcher(!showSwitcher)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <Building2 className="w-4 h-4 text-white/70" />
                    <span className="text-white text-sm font-medium max-w-[120px] truncate">
                      {currentBusiness?.name || currentBusiness?.business_name || 'Select Business'}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${showSwitcher ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showSwitcher && (
                    <div 
                      className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                      style={{ boxShadow: '0 10px 40px rgba(30, 27, 75, 0.2)' }}
                    >
                      <div className="p-2">
                        <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          Your Businesses
                        </p>
                        {businesses.map((biz) => (
                          <button
                            key={biz.id}
                            onClick={() => handleBusinessSelect(biz.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                              currentBusiness?.id === biz.id 
                                ? 'bg-[#8b5cf6]/10 text-[#8b5cf6]' 
                                : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              currentBusiness?.id === biz.id ? 'bg-[#8b5cf6]/20' : 'bg-gray-100'
                            }`}>
                              <Building2 className={`w-4 h-4 ${
                                currentBusiness?.id === biz.id ? 'text-[#8b5cf6]' : 'text-gray-500'
                              }`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{biz.name || biz.business_name}</p>
                              <p className="text-xs text-gray-400 truncate">{biz.category || 'Business'}</p>
                            </div>
                            {currentBusiness?.id === biz.id && (
                              <div className="w-2 h-2 bg-[#8b5cf6] rounded-full"></div>
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="border-t border-gray-100 p-2">
                        <button
                          onClick={() => navigate('/business/onboarding')}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Plus className="w-4 h-4 text-gray-500" />
                          </div>
                          <span className="font-medium">Add New Business</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Desktop: Divider + Homepage + Sign out */}
              <div className="hidden md:flex items-center gap-4">
                <div className="h-5 w-px bg-white/20"></div>
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
                >
                  <Home className="w-4 h-4" />
                  <span>Homepage</span>
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign out</span>
                </button>
              </div>
              
              {/* Mobile: Hamburger Menu */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                className="md:hidden w-10 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all rounded-lg"
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Mobile Drawer */}
      <DashboardMobileDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        variant="business"
      >
        <DashboardMobileNav
          variant="business"
          user={user}
          onClose={() => setMobileMenuOpen(false)}
          businesses={flag('MULTI_BUSINESS') ? businesses : []}
          currentBusiness={currentBusiness}
          onBusinessSelect={(bizId) => {
            handleBusinessSelect(bizId);
            setMobileMenuOpen(false);
          }}
          badges={badges}
        />
      </DashboardMobileDrawer>
    </>
  );
};

export default BusinessHeader;
